<template>
	<ui-box v-if="orders && orders.length">
		<h6>{{ title }}</h6>

		<div class="my-2"></div>
		<v-row>
			<v-col cols="2">
				Sort by:
			</v-col>
			<v-col>
				Order Id
				<span>
					<v-btn
							class="ml-1"
							color="secondary"
							depressed
							icon
							small
							@click.stop="sortOrders('id', 'asc')"
					>
						<v-icon small>mdi-chevron-up-box-outline</v-icon>
					</v-btn>
				</span>
				<span>
					<v-btn
							class="ml-1"
							color="secondary"
							depressed
							icon
							small
							@click.stop="sortOrders('id', 'desc')"
					>
						<v-icon small>mdi-chevron-down-box-outline</v-icon>
					</v-btn>
				</span>
			</v-col>
			<v-col>
				Purchase Date
				<span>
					<v-btn
							class="ml-1"
							color="secondary"
							depressed
							icon
							small
							@click.stop="sortOrders('purchased_at', 'asc')"
					>
						<v-icon small>mdi-chevron-up-box-outline</v-icon>
					</v-btn>
				</span>
				<span>
					<v-btn
							class="ml-1"
							color="secondary"
							depressed
							icon
							small
							@click.stop="sortOrders('purchased_at', 'desc')"
					>
						<v-icon small>mdi-chevron-down-box-outline</v-icon>
					</v-btn>
				</span>
			</v-col>
		</v-row>
		<div class="my-4"></div>

		<ui-box v-for="order in orders" :key="`order-${order.id}`">
			<v-row no-gutters>
				<v-col align-self="start" cols="4" md="12">
					<span class="font-weight-bold text-body-2 text-md-h6">Order Id</span>
				</v-col>
				<v-col align-self="start" class="pt-1 text-body-2" cols="8" md="12">
					<a target="_blank" :href="orderViewUrl(order.id)">{{
							order.id
						}}</a>
				</v-col>
			</v-row>
			<v-divider class="my-2"></v-divider>

			<template v-if="getOfferPoints(order.offer_id) != null">
				<v-row no-gutters>
					<v-col align-self="start" cols="4" md="12">
						<span class="font-weight-bold text-body-2 text-md-h6">Offer Points</span>
					</v-col>
					<v-col align-self="start" class="pt-1 text-body-2" cols="8" md="12">
						{{ getOfferPoints(order.offer_id) }}
						<v-btn
								class="ml-1"
								color="secondary"
								depressed
								icon
								small
								@click.stop="openOfferPointsEdit(order.offer_id)"
						>
							<v-icon>mdi-pencil-outline</v-icon>
						</v-btn>
					</v-col>
				</v-row>
				<v-divider class="my-2"></v-divider>
			</template>

			<v-row no-gutters>
				<v-col align-self="start" cols="4" md="12">
					<span class="font-weight-bold text-body-2 text-md-h6">Purchased</span>
				</v-col>
				<v-col align-self="start" class="pt-1 text-body-2" cols="8" md="12">
					{{ getLocalDateTime(order.purchased_at, order.partner.timezone) }}
				</v-col>
			</v-row>
			<v-divider class="my-2"></v-divider>

			<v-row no-gutters>
				<v-col align-self="start" cols="4" md="12">
					<span class="font-weight-bold text-body-2 text-md-h6">Status</span>
				</v-col>
				<v-col align-self="start" class="pt-1 text-body-2" cols="8" md="12">
					{{ order.status }}
				</v-col>
			</v-row>
			<v-divider class="my-2"></v-divider>

			<v-row no-gutters>
				<v-col align-self="start" cols="4" md="12">
					<span class="font-weight-bold text-body-2 text-md-h6">Items</span>
				</v-col>
				<v-col align-self="start" class="pt-1 text-body-2" cols="8" md="12">
					<template v-if="order.r_order_packages && order.r_order_packages.length">
						<template v-for="(orderPackage, idx) in order.r_order_packages">
							<br v-if="idx > 0">
							<a target="_blank"
								 :href="viewAdminUrl(order.partner_id) + '/package/' + orderPackage.package_id">{{
									packageName(orderPackage)
								}}</a>
						</template>
						<br>
					</template>
					<template v-if="order.r_order_deals && order.r_order_deals.length">
						<template v-for="(orderDeal, idx) in order.r_order_deals">
							<br v-if="idx > 0">
							<a target="_blank"
								 :href="viewAdminUrl(order.partner_id) + '/deal/' + orderDeal.deal_id">{{
									passName(orderDeal)
								}}</a>
							<template v-if="getDealPoints(orderDeal.deal_id) != null">
								<span class="text-body-2">Points: {{
										getDealPoints(orderDeal.deal_id)
									}}</span>
								<span>
									<v-btn
											class="ml-1"
											color="secondary"
											depressed
											icon
											small
											@click.stop="openDealPointsEdit(orderDeal.deal_id)"
									>
										<v-icon>mdi-pencil-outline</v-icon>
									</v-btn>
								</span>
							</template>
						</template>
						<br>
					</template>
					<template v-if="order.r_order_products && order.r_order_products.length">
						<template v-for="(orderProduct, idx) in order.r_order_products">
							<br v-if="idx > 0">
							<a target="_blank"
								 :href="viewAdminUrl(order.partner_id) + '/product/' + orderProduct.product_id">{{
									standaloneProductName(orderProduct)
								}}</a>
						</template>
					</template>
				</v-col>
			</v-row>

		</ui-box>

		<div class="my-2"></div>

		<v-dialog v-model="offerPointsEdit" content-class="rounded-xl" scrollable
							width="600px">
			<v-card class="position-relative" rounded="xl">
				<v-card-title class="d-flex justify-space-between">
					<h5>Edit Offer Points</h5>
					<v-btn
							class="mt-n1 me-n2"
							color="primary"
							depressed
							icon
							@click="closeOfferPointsEdit()"
					>
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-card-text>
					<v-form v-model="formOfferPointsEditIsValid"
									ref="formOfferPointsEdit">
						<v-row no-gutters>
							<v-col>
								<v-text-field
										v-model.number="points.totalPoints"
										class="mt-4 mt-sm-0 rounded-lg"
										label="Total Reward Points"
										outlined
										readonly
										disabled
										persistent-hint
										type="number"
										@wheel.native.prevent
								></v-text-field>
							</v-col>
						</v-row>
						<v-row no-gutters>
							<v-col>
								<v-text-field
										v-model.number="points.pointsToAdd"
										class="rounded-lg"
										label="Add Points"
										outlined
										persistent-hint
										type="number"
										@wheel.native.prevent
								></v-text-field>
							</v-col>
						</v-row>
						<v-row no-gutters>
							<v-col>
								<v-text-field
										v-model.number="points.pointsToSubtract"
										class="rounded-lg"
										label="Substract Points"
										outlined
										persistent-hint
										type="number"
										@wheel.native.prevent
								></v-text-field>
							</v-col>
						</v-row>
						<v-row no-gutters>
							<v-col>
								<v-text-field
										v-model="points.reason"
										class="rounded-lg required"
										label="Reason for Change"
										outlined
										persistent-hint
										type="text"
										:rules="rules.points_reason"
								></v-text-field>
							</v-col>
						</v-row>
					</v-form>
				</v-card-text>
				<v-card-actions class="grey lighten-4">
					<v-spacer></v-spacer>
					<v-btn
							color="secondary"
							text
							@click="closeOfferPointsEdit()"
					>
						Cancel
					</v-btn>
					<v-btn
							color="secondary"
							text
							:disabled="!formOfferPointsEditIsValid"
							@click="updateOfferPoints()"
					>
						Update
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<v-dialog v-model="dealPointsEdit" content-class="rounded-xl" scrollable
							width="600px">
			<v-card class="position-relative" rounded="xl">
				<v-card-title class="d-flex justify-space-between">
					<h5>Edit Deal Points</h5>
					<v-btn
							class="mt-n1 me-n2"
							color="primary"
							depressed
							icon
							@click="closeDealPointsEdit()"
					>
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-card-text>
					<v-form v-model="formDealPointsEditIsValid"
									ref="formDealPointsEdit">
						<v-row no-gutters>
							<v-col>
								<v-text-field
										v-model.number="points.totalPoints"
										class="mt-4 mt-sm-0 rounded-lg"
										label="Total Reward Points"
										outlined
										readonly
										disabled
										persistent-hint
										type="number"
										@wheel.native.prevent
								></v-text-field>
							</v-col>
						</v-row>
						<v-row no-gutters>
							<v-col>
								<v-text-field
										v-model.number="points.pointsToAdd"
										class="rounded-lg"
										label="Add Points"
										outlined
										persistent-hint
										type="number"
										@wheel.native.prevent
								></v-text-field>
							</v-col>
						</v-row>
						<v-row no-gutters>
							<v-col>
								<v-text-field
										v-model.number="points.pointsToSubtract"
										class="rounded-lg"
										label="Substract Points"
										outlined
										persistent-hint
										type="number"
										@wheel.native.prevent
								></v-text-field>
							</v-col>
						</v-row>
						<v-row no-gutters>
							<v-col>
								<v-text-field
										v-model="points.reason"
										class="rounded-lg required"
										label="Reason for Change"
										outlined
										persistent-hint
										type="text"
										:rules="rules.points_reason"
								></v-text-field>
							</v-col>
						</v-row>
					</v-form>
				</v-card-text>
				<v-card-actions class="grey lighten-4">
					<v-spacer></v-spacer>
					<v-btn
							color="secondary"
							text
							@click="closeDealPointsEdit()"
					>
						Cancel
					</v-btn>
					<v-btn
							color="secondary"
							text
							:disabled="!formDealPointsEditIsValid"
							@click="updateDealPoints()"
					>
						Update
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>


	</ui-box>
</template>

<script>

import { helpers } from '../../../helpers';

export default {
	name: "CustomerOrdersTable",
	props: {
		partner: {
			type: Object,
			default: () => ({})
		},
		customer: {
			type: Object,
			default: () => ({})
		},
		extraData: {
			type: Object,
			default: () => ({})
		},
		orders: {
			type: Array,
			default: () => []
		},
		title: {
			type: String,
			default: ''
		},
	},
	data() {
		return {
			offerPointsEdit: false,
			formOfferPointsEditIsValid: false,
			dealPointsEdit: false,
			formDealPointsEditIsValid: false,
			points: new Points(),
			rules: {
				points_reason: [
					v => !!v || 'Reason for change is required',
				],
			},
		}
	},
	methods: {
		orderViewUrl(orderId) {
			return helpers.getBaseUrl(this.partner, 'customers', 'main') + '/view-order/' + orderId;
		},
		getLocalDateTime(dateTime, tz) {
			return helpers.getLocalDateTime(dateTime, tz);
		},
		viewAdminUrl(partnerId) {
			return '/admin/' + partnerId;
		},
		packageName(orderPackage) {
			let attrName = '';
			if(orderPackage.attribute?.name) {
				attrName = ' - ' + orderPackage.attribute.name;
			}
			return orderPackage.package.name + attrName;
		},
		passName(orderDeal) {
			return orderDeal.deal.name + this.addAttributeName(orderDeal) + this.addPackageName(orderDeal);
		},
		standaloneProductName(orderProduct) {
			return orderProduct.product.name + this.addAttributeName(orderProduct) + this.addPackageName(orderProduct);
		},
		addAttributeName(item) {
			return item.attribute?.name ? ' - ' + item.attribute.name : '';
		},
		addPackageName(item) {
			return item.package?.name ? ' [' + item.package?.name + ']' : '';
		},
		getOfferPoints(offerId) {
			if(this.extraData.pointsPerOffer && this.extraData.pointsPerOffer.length) {
				const offer = this.extraData.pointsPerOffer.find(item => item.id === offerId);
				if(offer) {
					return offer.points;
				}
			}
			return null;
		},
		getDealPoints(dealId) {
			if(this.extraData.pointsPerDeal && this.extraData.pointsPerDeal.length) {
				const deal = this.extraData.pointsPerDeal.find(item => item.id === dealId);
				if(deal) {
					return deal.points;
				}
			}
			return null;
		},
		openOfferPointsEdit(offerId) {
			this.offerPointsEdit = true;
			this.points = new Points();
			this.points.offerId = offerId;
			this.points.totalPoints = this.getOfferPoints(offerId);
		},
		closeOfferPointsEdit() {
			this.$refs.formOfferPointsEdit.reset();
			this.offerPointsEdit = false;
		},
		updateOfferPoints() {
			this.$refs.formOfferPointsEdit.validate();
			if(!this.formOfferPointsEditIsValid) return;

			const url = `/partnerUsers/${this.customer.id}/offer/${this.points.offerId}/points`;
			if(this.points.pointsToAdd) {
				this.points.pointsToAdd = parseInt(this.points.pointsToAdd);
			}
			if(this.points.pointsToSubtract) {
				this.points.pointsToSubtract = parseInt(this.points.pointsToSubtract);
			}
			axios.post(url, this.points)
					.then(response => {
						const offer = this.extraData.pointsPerOffer.find(item => item.id === this.points.offerId);
						const offerResult = response.data.find(item => item.id === this.points.offerId);
						offer.points = offerResult.points;
						this.closeOfferPointsEdit();

						window.EventBus.$emit('system-messages', [
							{
								message: 'Offer points updated',
								messageType: 'success',
								showOverlay: true
							}
						]);
					})
					.catch(error => {
						this.closeOfferPointsEdit();
						window.EventBus.$emit('system-messages', [
							{ message: error.message, messageType: 'error' }
						]);
					});
		},
		openDealPointsEdit(dealId) {
			this.dealPointsEdit = true;
			this.points = new Points();
			this.points.dealId = dealId;
			this.points.totalPoints = this.getDealPoints(dealId);
		},
		closeDealPointsEdit() {
			this.dealPointsEdit = false;
		},
		updateDealPoints() {
			this.$refs.formDealPointsEdit.validate();
			if(!this.formDealPointsEditIsValid) return;

			const url = `/partnerUsers/${this.customer.id}/deal/${this.points.dealId}/points`;
			if(this.points.pointsToAdd) {
				this.points.pointsToAdd = parseInt(this.points.pointsToAdd);
			}
			if(this.points.pointsToSubtract) {
				this.points.pointsToSubtract = parseInt(this.points.pointsToSubtract);
			}
			axios.post(url, this.points)
					.then(response => {
						const deal = this.extraData.pointsPerDeal.find(item => item.id === this.points.dealId);
						const dealResult = response.data.find(item => item.id === this.points.dealId);
						deal.points = dealResult.points;

						this.closeDealPointsEdit();
						window.EventBus.$emit('system-messages', [
							{
								message: 'Deal points updated',
								messageType: 'success',
								showOverlay: true
							}
						]);
					})
					.catch(error => {
						this.closeDealPointsEdit();
						window.EventBus.$emit('system-messages', [
							{ message: error.message, messageType: 'error' }
						]);
					});
		},
		sortOrders(col, order) {
			this.orders.sort((a, b) => {
				let valA = a[col];
				let valB = b[col];
				if(col === 'purchased_at') {
					valA = new Date(valA);
					valB = new Date(valB);
				}
				if(valA < valB) {
					return order === 'asc' ? -1 : 1;
				}
				if(valA > valB) {
					return order === 'asc' ? 1 : -1;
				}
				return 0;
			});
		}
	}
}

function Points() {
	this.offerId = null;
	this.dealId = null;
	this.totalPoints = null;
	this.pointsToAdd = null;
	this.pointsToSubtract = null;
	this.reason = null;
}

</script>
